import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexMarkers,
  ApexStroke,
  ApexXAxis,
  ApexYAxis
} from 'ng-apexcharts';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { DialogControlePesosAlturaComponent } from 'src/app/components/dialog-controle-pesos-altura/dialog-controle-pesos-altura.component';
import { ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { BeControlePeso } from 'src/app/models/BeControlePeso';
import { DialogData } from 'src/app/models/dialog-data';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { ModoExibicaoPersistidoService } from 'src/app/services/modo-exibicao-persistido.service';

type ModoPeso = 'calendario' | 'lista' | 'grafico';

@Component({
  selector: 'app-controle-pesos',
  templateUrl: './controle-pesos.component.html',
  styleUrls: ['./controle-pesos.component.css']
})
export class ControlePesosComponent implements OnInit {
  public pesos: BeControlePeso[] = [];
  public modo: ModoPeso = 'calendario';
  public mes = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  public readonly hoje = new Date();
  public altura?: number;
  public editando?: BeControlePeso;
  public chaveDiaEdicao?: string;
  public readonly semanas = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  public colunas: ListaDadosColuna[] = [
    {
      campo: 'dtPesoData',
      rotulo: 'Data',
      tamanho: '50%',
      editavel: true,
      edicao: { tipoEditor: 'data', validacao: { obrigatorio: true } }
    },
    {
      campo: 'vlPeso',
      rotulo: 'Peso (kg)',
      tamanho: '50%',
      editavel: true,
      edicao: {
        tipoEditor: 'numero',
        validacao: { obrigatorio: true, min: 0, max: 300 }
      }
    }
  ];
  public chartSeries: ApexAxisChartSeries = [{ name: 'Peso (kg)', data: [] }];
  public chart: ApexChart = {
    type: 'line',
    height: 360,
    toolbar: { show: false },
    animations: { enabled: false }
  };
  public chartXaxis: ApexXAxis = {
    type: 'datetime',
    labels: {
      formatter: (_: string, timestamp?: number): string => {
        const data = new Date(timestamp ?? 0);
        const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

        return `${String(data.getDate()).padStart(2, '0')} ${meses[data.getMonth()]}`;
      }
    }
  };
  public chartYaxis: ApexYAxis = { title: { text: 'kg' } };
  public chartGrid: ApexGrid = { padding: { left: 24, right: 24 } };
  public chartStroke: ApexStroke = { curve: 'straight', width: 3 };
  public chartMarkers: ApexMarkers = {
    size: 5,
    colors: ['#1565c0'],
    strokeColors: '#1565c0',
    strokeWidth: 0,
    hover: { size: 6 }
  };
  public chartColors = ['#1565c0'];
  public chartDataLabels: ApexDataLabels = {
    enabled: true,
    offsetY: -12,
    formatter: (valor: number): string => `${valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })} kg`,
    style: {
      colors: ['#0d47a1'],
      fontSize: '12px',
      fontWeight: '600'
    },
    background: { enabled: false }
  };

  private readonly chaveModoExibicao = 'controle-pesos.modoExibicao';

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private snack: SnackBarService,
    private modoExibicaoPersistidoService: ModoExibicaoPersistidoService
  ) { }

  public ngOnInit(): void {
    if (this.modoExibicaoPersistidoService.deveRestaurarModoExibicao()) {
      const modo = sessionStorage.getItem(this.chaveModoExibicao);
      if (modo === 'calendario' || modo === 'lista' || modo === 'grafico')
        this.modo = modo;
    }
    else
      sessionStorage.setItem(this.chaveModoExibicao, 'calendario');
    this.carregar();
    this.carregarAltura();
  }

  public exibirModo(modo: ModoPeso): void {
    this.modo = modo;
    sessionStorage.setItem(this.chaveModoExibicao, modo);
  }

  public informarAltura(): void {
    this.dialog.open(DialogControlePesosAlturaComponent, {
      data: { altura: this.altura },
      width: '380px',
      maxWidth: 'calc(100vw - 32px)'
    }).afterClosed().subscribe(altura => {
      if (typeof altura !== 'number') {
        return;
      }

      this.http.put<{ altura: number }>('/api/ControlePesos/Altura', altura).subscribe({
        next: resposta => {
          this.altura = resposta.altura;
          this.snack.exibirSucesso('Altura salva!');
        },
        error: erro => this.snack.exibirErro(erro?.error?.message ?? 'Não foi possível salvar a altura.')
      });
    });
  }

  public baixarImagem(): void {
    const dados = [...this.pesos]
      .sort((primeiro, segundo) => primeiro.dtPeso.localeCompare(segundo.dtPeso))
      .slice(-12);

    if (!dados.length) {
      this.snack.exibirErro('Não há pesos registrados para gerar a imagem.');
      return;
    }

    const largura = 900;
    const altura = 500;
    const margem = { esquerda: 76, direita: 34, topo: 70, baixo: 78 };
    const valores = dados.map(peso => peso.vlPeso);
    const menorPeso = Math.min(...valores);
    const maiorPeso = Math.max(...valores);
    const centro = Math.round((menorPeso + maiorPeso) / 2);
    const semiFaixa = Math.max(centro - menorPeso, maiorPeso - centro) + 5;
    const passo = Math.max(1, Math.ceil(semiFaixa / 2));
    const minimo = Math.max(0, centro - passo * 2);
    const maximo = centro + passo * 2;
    const faixa = Math.max(maximo - minimo, 1);
    const areaLargura = largura - margem.esquerda - margem.direita;
    const areaAltura = altura - margem.topo - margem.baixo;
    const x = (indice: number): number => margem.esquerda + (dados.length === 1 ? areaLargura / 2 : indice * areaLargura / (dados.length - 1));
    const y = (valor: number): number => margem.topo + (maximo - valor) * areaAltura / faixa;
    const linha = dados.map((peso, indice) => `${x(indice)},${y(peso.vlPeso)}`).join(' ');
    const grades = Array.from({ length: 5 }, (_, indice) => minimo + faixa * indice / 4);
    const linhasGrade = grades.map(valor => {
      const posicao = y(valor);
      return `<line x1="${margem.esquerda}" y1="${posicao}" x2="${largura - margem.direita}" y2="${posicao}" stroke="#dbe5ef" />` +
        `<text x="${margem.esquerda - 12}" y="${posicao + 5}" text-anchor="end" class="eixo">${this.formatarPeso(valor)}</text>`;
    }).join('');
    const pontos = dados.map((peso, indice) => {
      const posicaoX = x(indice);
      const rotuloX = indice === 0 && dados.length > 1 ? posicaoX + 12 : indice === dados.length - 1 && dados.length > 1 ? posicaoX - 12 : posicaoX;
      const ancoraRotulo = indice === 0 && dados.length > 1 ? 'start' : indice === dados.length - 1 && dados.length > 1 ? 'end' : 'middle';

      return `<circle cx="${posicaoX}" cy="${y(peso.vlPeso)}" r="5" fill="#1565c0" />` +
        `<text x="${rotuloX}" y="${y(peso.vlPeso) - 12}" text-anchor="${ancoraRotulo}" class="valor">${this.formatarPeso(peso.vlPeso)} kg</text>` +
        `<text x="${posicaoX}" y="${altura - 35}" text-anchor="middle" class="eixo">${this.formatarData(peso.dtPeso)}</text>`;
    }).join('');
    const svg = `<svg width="${largura}" height="${altura}" xmlns="http://www.w3.org/2000/svg">
      <style>.titulo{font:700 26px Arial;fill:#1f2933}.subtitulo{font:16px Arial;fill:#5f6b76}.eixo{font:14px Arial;fill:#607080}.valor{font:600 14px Arial;fill:#0d47a1}</style>
      <rect width="100%" height="100%" fill="#fff" rx="16" />
      <text x="${margem.esquerda}" y="34" class="titulo">Evolução</text>
      <text x="${margem.esquerda}" y="57" class="subtitulo">Últimos registros</text>
      ${linhasGrade}
      <polyline points="${linha}" fill="none" stroke="#1565c0" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
      ${pontos}
    </svg>`;
    const imagem = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));

    imagem.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = largura;
      canvas.height = altura;
      canvas.getContext('2d')?.drawImage(imagem, 0, 0);
      URL.revokeObjectURL(url);
      const link = document.createElement('a');
      link.download = 'evolucao-peso.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    imagem.src = url;
  }

  public baixarImagemCalendario(): void {
    const hoje = new Date(this.hoje.getFullYear(), this.hoje.getMonth(), this.hoje.getDate());
    const inicio = hoje.getDate() < 15
      ? new Date(hoje.getFullYear(), hoje.getMonth() - 1, 15)
      : new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    const dados = this.pesos
      .filter(peso => {
        const data = this.dataPeso(peso);
        return data >= inicio && data <= fim;
      })
      .sort((primeiro, segundo) => primeiro.dtPeso.localeCompare(segundo.dtPeso));
    const pesosOrdenados = [...this.pesos]
      .sort((primeiro, segundo) => primeiro.dtPeso.localeCompare(segundo.dtPeso));

    if (!dados.length) {
      this.snack.exibirErro('Não há pesos registrados neste período para gerar a imagem.');
      return;
    }

    const largura = 1600;
    const margem = 60;
    const topo = 340;
    const espaco = 12;
    const totalDias = Math.round((fim.getTime() - inicio.getTime()) / 86400000) + 1;
    const totalSemanas = Math.ceil((inicio.getDay() + totalDias) / 7);
    const larguraCelula = (largura - margem * 2 - espaco * 6) / 7;
    const alturaCelula = 132;
    const altura = topo + totalSemanas * (alturaCelula + espaco) + margem - espaco;
    const ultimoPeso = dados[dados.length - 1];
    const variacaoMes = ultimoPeso.vlPeso - dados[0].vlPeso;
    const imc = this.obterInformacaoImc(ultimoPeso.vlPeso);
    const canvas = document.createElement('canvas');
    const contexto = canvas.getContext('2d');

    if (!contexto) {
      return;
    }

    canvas.width = largura;
    canvas.height = altura;
    const arredondar = (x: number, y: number, larguraCaixa: number, alturaCaixa: number, raio: number): void => {
      contexto.beginPath();
      contexto.moveTo(x + raio, y);
      contexto.lineTo(x + larguraCaixa - raio, y);
      contexto.quadraticCurveTo(x + larguraCaixa, y, x + larguraCaixa, y + raio);
      contexto.lineTo(x + larguraCaixa, y + alturaCaixa - raio);
      contexto.quadraticCurveTo(x + larguraCaixa, y + alturaCaixa, x + larguraCaixa - raio, y + alturaCaixa);
      contexto.lineTo(x + raio, y + alturaCaixa);
      contexto.quadraticCurveTo(x, y + alturaCaixa, x, y + alturaCaixa - raio);
      contexto.lineTo(x, y + raio);
      contexto.quadraticCurveTo(x, y, x + raio, y);
      contexto.closePath();
    };
    const posicaoCelula = (semana: number, diaSemana: number): { x: number; y: number } => ({
      x: margem + diaSemana * (larguraCelula + espaco),
      y: topo + semana * (alturaCelula + espaco)
    });
    const variacao = (peso: BeControlePeso): number | undefined => {
      const indice = pesosOrdenados.findIndex(item => item.id === peso.id);
      return indice > 0 ? peso.vlPeso - pesosOrdenados[indice - 1].vlPeso : undefined;
    };
    const corVariacao = (valor: number): string => valor < 0 ? '#169b62' : valor > 0 ? '#e5484d' : '#64748b';
    const textoVariacao = (valor: number): string => `${valor > 0 ? '+' : valor < 0 ? '-' : ''}${this.formatarPeso(Math.abs(valor))} kg`;
    const desenharPill = (texto: string, x: number, y: number, corFundo: string, corTexto: string): void => {
      contexto.font = '600 17px Arial';
      const larguraPill = contexto.measureText(texto).width + 28;
      arredondar(x - larguraPill, y, larguraPill, 31, 15);
      contexto.fillStyle = corFundo;
      contexto.fill();
      contexto.fillStyle = corTexto;
      contexto.textAlign = 'center';
      contexto.fillText(texto, x - larguraPill / 2, y + 21);
    };
    const desenharIndicador = (x: number, rotulo: string, valor: string, cor: string, detalhe?: string): void => {
      arredondar(x, 88, 200, 112, 20);
      contexto.fillStyle = '#fff';
      contexto.globalAlpha = .15;
      contexto.fill();
      contexto.globalAlpha = 1;
      contexto.fillStyle = '#dbeafe';
      contexto.font = '600 15px Arial';
      contexto.textAlign = 'left';
      contexto.fillText(rotulo.toUpperCase(), x + 22, 122);
      contexto.fillStyle = '#fff';
      contexto.font = 'bold 29px Arial';
      contexto.fillText(valor, x + 22, 160);
      if (detalhe) {
        contexto.fillStyle = '#dbeafe';
        contexto.font = 'bold 12px Arial';
        contexto.fillText(detalhe, x + 22, 184);
      }
      contexto.fillStyle = cor;
      contexto.beginPath();
      contexto.arc(x + 170, 112, 8, 0, Math.PI * 2);
      contexto.fill();
    };
    const nomeMes = (data: Date): string => data.toLocaleDateString('pt-BR', { month: 'short' })
      .replace('.', '')
      .replace(/^./, letra => letra.toUpperCase());
    const titulo = inicio.getMonth() === fim.getMonth()
      ? `${nomeMes(fim)}/${String(fim.getFullYear()).slice(-2)}`
      : `${nomeMes(inicio)}/${String(inicio.getFullYear()).slice(-2)}~${nomeMes(fim)}/${String(fim.getFullYear()).slice(-2)}`;

    contexto.fillStyle = '#f2f5fb';
    contexto.fillRect(0, 0, largura, altura);
    const cabecalho = contexto.createLinearGradient(0, 0, largura, 240);
    cabecalho.addColorStop(0, '#172554');
    cabecalho.addColorStop(.55, '#3346aa');
    cabecalho.addColorStop(1, '#7c3aed');
    arredondar(margem, margem, largura - margem * 2, 190, 28);
    contexto.fillStyle = cabecalho;
    contexto.fill();
    contexto.save();
    contexto.globalAlpha = .14;
    contexto.strokeStyle = '#fff';
    contexto.lineWidth = 2;
    for (let indice = 0; indice < 6; indice++) {
      contexto.beginPath();
      contexto.arc(180 + indice * 75, 150, 115 + indice * 12, Math.PI * 1.12, Math.PI * 1.84);
      contexto.stroke();
    }
    contexto.restore();
    contexto.fillStyle = '#fff';
    contexto.font = 'bold 52px Arial';
    contexto.textAlign = 'left';
    contexto.fillText(titulo, margem + 38, 145);
    contexto.fillStyle = '#dbeafe';
    contexto.font = 'bold 16px Arial';
    contexto.fillText('RUN  •  LIFT  •  REPEAT', margem + 42, 181);
    desenharIndicador(680, 'Último peso', `${this.formatarPeso(ultimoPeso.vlPeso)} kg`, '#8bdbbd');
    if (imc) desenharIndicador(895, `IMC • ${imc.estado}`, this.formatarPeso(imc.valor), imc.cor, `Normal: ${this.formatarPeso(imc.pesoMinimo)}–${this.formatarPeso(imc.pesoMaximo)} kg`);
    desenharIndicador(imc ? 1110 : 1030, 'Variação no mês', textoVariacao(variacaoMes), corVariacao(variacaoMes));
    desenharIndicador(imc ? 1325 : 1280, 'Registros', String(dados.length).padStart(2, '0'), '#fcd34d');

    this.semanas.forEach((semana, indice) => {
      const { x } = posicaoCelula(0, indice);
      arredondar(x, topo - 45, larguraCelula, 33, 12);
      contexto.fillStyle = indice === 0 || indice === 6 ? '#e8eefc' : '#fff';
      contexto.fill();
      contexto.fillStyle = '#475569';
      contexto.font = 'bold 15px Arial';
      contexto.textAlign = 'center';
      contexto.fillText(semana.toUpperCase(), x + larguraCelula / 2, topo - 23);
    });

    for (let semana = 0; semana < totalSemanas; semana++) {
      for (let diaSemana = 0; diaSemana < 7; diaSemana++) {
        const { x, y } = posicaoCelula(semana, diaSemana);
        const indice = semana * 7 + diaSemana - inicio.getDay();
        const data = indice >= 0 && indice < totalDias ? new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + indice) : undefined;
        const peso = data ? dados.find(item => this.mesmoDia(this.dataPeso(item), data)) : undefined;
        const diferenca = peso ? variacao(peso) : undefined;
        const diaAtual = data && this.mesmoDia(data, hoje);

        arredondar(x, y, larguraCelula, alturaCelula, 18);
        contexto.fillStyle = !data ? '#edf1f7' : diaAtual && diferenca !== undefined ? diferenca < 0 ? '#e9f9f1' : diferenca > 0 ? '#fff0f1' : '#f8fafc' : data.getDay() === 0 || data.getDay() === 6 ? '#f8faff' : '#fff';
        contexto.fill();
        if (diaAtual && diferenca !== undefined) {
          contexto.save();
          arredondar(x + 4, y + 4, larguraCelula - 8, alturaCelula - 8, 15);
          contexto.strokeStyle = corVariacao(diferenca);
          contexto.shadowColor = corVariacao(diferenca);
          contexto.shadowBlur = 16;
          contexto.globalAlpha = .7;
          contexto.lineWidth = 3;
          contexto.stroke();
          contexto.restore();
        }
        if (peso) {
          contexto.fillStyle = diferenca === undefined ? '#5b6dfc' : corVariacao(diferenca);
          arredondar(x, y + 18, 5, alturaCelula - 36, 3);
          contexto.fill();
        }
      }
    }

    for (let semana = 0; semana < totalSemanas; semana++) {
      for (let diaSemana = 0; diaSemana < 7; diaSemana++) {
        const { x, y } = posicaoCelula(semana, diaSemana);
        const indice = semana * 7 + diaSemana - inicio.getDay();
        const data = indice >= 0 && indice < totalDias ? new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + indice) : undefined;
        const peso = data ? dados.find(item => this.mesmoDia(this.dataPeso(item), data)) : undefined;

        if (!data) {
          continue;
        }

        contexto.fillStyle = '#1e293b';
        contexto.font = 'bold 24px Arial';
        contexto.textAlign = 'left';
        contexto.fillText(String(data.getDate()), x + 17, y + 32);
        if (data.getDate() === 1 || this.mesmoDia(data, inicio)) {
          contexto.fillStyle = '#94a3b8';
          contexto.font = 'bold 12px Arial';
          contexto.fillText(nomeMes(data).toUpperCase(), x + 18, y + 52);
        }
        if (peso) {
          desenharPill(`${this.formatarPeso(peso.vlPeso)} kg`, x + larguraCelula - 15, y + 15, '#e8edff', '#3447bb');
          const diferenca = variacao(peso);
          if (diferenca !== undefined) {
            const cor = corVariacao(diferenca);
            desenharPill(textoVariacao(diferenca), x + larguraCelula - 15, y + 52, `${cor}1c`, cor);
          }
          contexto.fillStyle = '#94a3b8';
          contexto.font = 'bold 11px Arial';
          contexto.textAlign = 'left';
          contexto.fillText('CHECK-IN', x + 18, y + alturaCelula - 18);
          contexto.fillStyle = diferenca === undefined ? '#5b6dfc' : corVariacao(diferenca);
          for (let marcador = 0; marcador < 3; marcador++) {
            contexto.beginPath();
            contexto.arc(x + larguraCelula - 52 + marcador * 13, y + alturaCelula - 22, 3, 0, Math.PI * 2);
            contexto.fill();
          }
        }
      }
    }

    const link = document.createElement('a');
    link.download = `calendario-peso-${titulo.replace('~', '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  public baixarImagemRelatorioMensal(): void {
    const dados = this.pesos
      .filter(peso => {
        const data = this.dataPeso(peso);
        return data.getFullYear() === this.mes.getFullYear() && data.getMonth() === this.mes.getMonth();
      })
      .sort((primeiro, segundo) => primeiro.dtPeso.localeCompare(segundo.dtPeso));

    if (!dados.length) {
      this.snack.exibirErro('Não há pesos registrados neste mês para gerar o relatório.');
      return;
    }

    const pesosOrdenados = [...this.pesos]
      .sort((primeiro, segundo) => primeiro.dtPeso.localeCompare(segundo.dtPeso));
    const primeiroDia = new Date(this.mes.getFullYear(), this.mes.getMonth(), 1).getDay();
    const ultimoDia = new Date(this.mes.getFullYear(), this.mes.getMonth() + 1, 0).getDate();
    const totalSemanas = Math.ceil((primeiroDia + ultimoDia) / 7);
    const semanas = Array.from({ length: totalSemanas }, (_, indice) => {
      const inicio = Math.max(1, indice * 7 - primeiroDia + 1);
      const fim = Math.min(ultimoDia, inicio + 6);
      const registros = dados.filter(peso => {
        const dia = this.dataPeso(peso).getDate();
        return dia >= inicio && dia <= fim;
      });
      const ultimo = registros[registros.length - 1];
      const indiceUltimo = ultimo ? pesosOrdenados.findIndex(peso => peso.id === ultimo.id) : -1;
      const anterior = indiceUltimo > 0 ? pesosOrdenados[indiceUltimo - 1] : undefined;

      return {
        inicio,
        fim,
        registros,
        ultimo,
        variacao: ultimo && anterior ? ultimo.vlPeso - anterior.vlPeso : undefined
      };
    });
    const ultimoPeso = dados[dados.length - 1];
    const variacaoMes = ultimoPeso.vlPeso - dados[0].vlPeso;
    const melhorSemana = semanas
      .filter(semana => semana.variacao !== undefined && semana.variacao < 0)
      .sort((primeira, segunda) => primeira.variacao! - segunda.variacao!)[0];
    const largura = 1600;
    const altura = 1220;
    const margem = 70;
    const colunas = 3;
    const espaco = 24;
    const larguraCard = (largura - margem * 2 - espaco * (colunas - 1)) / colunas;
    const alturaCard = 330;
    const canvas = document.createElement('canvas');
    const contexto = canvas.getContext('2d');

    if (!contexto) {
      return;
    }

    const arredondar = (x: number, y: number, larguraCaixa: number, alturaCaixa: number, raio: number): void => {
      contexto.beginPath();
      contexto.moveTo(x + raio, y);
      contexto.lineTo(x + larguraCaixa - raio, y);
      contexto.quadraticCurveTo(x + larguraCaixa, y, x + larguraCaixa, y + raio);
      contexto.lineTo(x + larguraCaixa, y + alturaCaixa - raio);
      contexto.quadraticCurveTo(x + larguraCaixa, y + alturaCaixa, x + larguraCaixa - raio, y + alturaCaixa);
      contexto.lineTo(x + raio, y + alturaCaixa);
      contexto.quadraticCurveTo(x, y + alturaCaixa, x, y + alturaCaixa - raio);
      contexto.lineTo(x, y + raio);
      contexto.quadraticCurveTo(x, y, x + raio, y);
      contexto.closePath();
    };
    const corVariacao = (valor: number): string => valor < 0 ? '#10b981' : valor > 0 ? '#f43f5e' : '#64748b';
    const textoVariacao = (valor: number): string => `${valor > 0 ? '+' : valor < 0 ? '-' : ''}${this.formatarPeso(Math.abs(valor))} kg`;
    const nomeMes = this.mes.toLocaleDateString('pt-BR', { month: 'long' });
    const tituloMes = `${nomeMes[0].toUpperCase() + nomeMes.slice(1)} ${this.mes.getFullYear()}`;

    canvas.width = largura;
    canvas.height = altura;
    contexto.fillStyle = '#f4f7fb';
    contexto.fillRect(0, 0, largura, altura);
    const cabecalho = contexto.createLinearGradient(0, 0, largura, 270);
    cabecalho.addColorStop(0, '#062c2a');
    cabecalho.addColorStop(.55, '#0c765c');
    cabecalho.addColorStop(1, '#48a742');
    arredondar(margem, margem, largura - margem * 2, 230, 34);
    contexto.fillStyle = cabecalho;
    contexto.fill();
    contexto.save();
    contexto.globalAlpha = .14;
    contexto.strokeStyle = '#fff';
    contexto.lineWidth = 3;
    for (let indice = 0; indice < 6; indice++) {
      contexto.beginPath();
      contexto.arc(210 + indice * 85, 195, 130 + indice * 14, Math.PI * 1.1, Math.PI * 1.82);
      contexto.stroke();
    }
    contexto.restore();
    contexto.fillStyle = '#b9f8d4';
    contexto.font = 'bold 18px Arial';
    contexto.fillText('RELATÓRIO MENSAL  •  PERFORMANCE', margem + 42, 128);
    contexto.fillStyle = '#fff';
    contexto.font = 'bold 49px Arial';
    contexto.fillText(tituloMes, margem + 42, 186);
    contexto.fillStyle = '#d5f5df';
    contexto.font = 'bold 17px Arial';
    contexto.fillText('DISCIPLINA  •  CONSTÂNCIA  •  EVOLUÇÃO', margem + 44, 225);

    const indicador = (x: number, rotulo: string, valor: string, cor: string): void => {
      arredondar(x, 109, 235, 116, 22);
      contexto.fillStyle = '#fff';
      contexto.globalAlpha = .14;
      contexto.fill();
      contexto.globalAlpha = 1;
      contexto.fillStyle = '#d8f8e6';
      contexto.font = 'bold 15px Arial';
      contexto.fillText(rotulo.toUpperCase(), x + 22, 143);
      contexto.fillStyle = '#fff';
      contexto.font = 'bold 30px Arial';
      contexto.fillText(valor, x + 22, 186);
      contexto.fillStyle = cor;
      contexto.beginPath();
      contexto.arc(x + 204, 133, 8, 0, Math.PI * 2);
      contexto.fill();
    };

    indicador(785, 'Último peso', `${this.formatarPeso(ultimoPeso.vlPeso)} kg`, '#b9f8d4');
    indicador(1038, 'Variação mensal', textoVariacao(variacaoMes), corVariacao(variacaoMes));
    indicador(1291, 'Check-ins', String(dados.length).padStart(2, '0'), '#fde68a');

    semanas.forEach((semana, indice) => {
      const coluna = indice % colunas;
      const linha = Math.floor(indice / colunas);
      const x = margem + coluna * (larguraCard + espaco);
      const y = 350 + linha * (alturaCard + espaco);
      const cor = semana.variacao === undefined ? '#64748b' : corVariacao(semana.variacao);
      const status = semana.variacao === undefined ? 'SEM BASE DE COMPARAÇÃO' : semana.variacao < 0 ? 'EVOLUÇÃO POSITIVA' : semana.variacao > 0 ? 'ATENÇÃO À ROTA' : 'RITMO ESTÁVEL';

      contexto.save();
      contexto.shadowColor = '#cbd5e1';
      contexto.shadowBlur = 16;
      contexto.shadowOffsetY = 6;
      arredondar(x, y, larguraCard, alturaCard, 26);
      contexto.fillStyle = '#fff';
      contexto.fill();
      contexto.restore();
      arredondar(x, y, larguraCard, 76, 26);
      contexto.fillStyle = `${cor}18`;
      contexto.fill();
      contexto.fillStyle = cor;
      arredondar(x, y + 18, 7, 40, 4);
      contexto.fill();
      contexto.font = 'bold 17px Arial';
      contexto.fillText(`SEMANA ${String(indice + 1).padStart(2, '0')}`, x + 28, y + 34);
      contexto.fillStyle = '#64748b';
      contexto.font = 'bold 15px Arial';
      contexto.fillText(`${String(semana.inicio).padStart(2, '0')}—${String(semana.fim).padStart(2, '0')} DE ${nomeMes.toUpperCase()}`, x + 28, y + 59);
      arredondar(x + larguraCard - 196, y + 18, 168, 31, 15);
      contexto.fillStyle = `${cor}1a`;
      contexto.fill();
      contexto.fillStyle = cor;
      contexto.font = 'bold 13px Arial';
      contexto.textAlign = 'center';
      contexto.fillText(status, x + larguraCard - 112, y + 39);
      contexto.textAlign = 'left';

      if (semana.ultimo) {
        contexto.fillStyle = '#172033';
        contexto.font = 'bold 47px Arial';
        contexto.fillText(`${this.formatarPeso(semana.ultimo.vlPeso)} kg`, x + 28, y + 151);
        contexto.fillStyle = '#64748b';
        contexto.font = 'bold 15px Arial';
        contexto.fillText('ÚLTIMO REGISTRO DA SEMANA', x + 30, y + 180);
        contexto.fillStyle = cor;
        contexto.font = 'bold 27px Arial';
        contexto.fillText(semana.variacao === undefined ? '—' : textoVariacao(semana.variacao), x + 28, y + 230);
        contexto.fillStyle = '#64748b';
        contexto.font = '16px Arial';
        contexto.fillText('vs. último check-in anterior', x + 28, y + 257);
        contexto.fillStyle = '#e5eaf0';
        arredondar(x + 28, y + 280, larguraCard - 56, 12, 6);
        contexto.fill();
        contexto.fillStyle = cor;
        const progresso = Math.min(1, semana.registros.length / 4);
        arredondar(x + 28, y + 280, (larguraCard - 56) * progresso, 12, 6);
        contexto.fill();
        contexto.fillStyle = '#64748b';
        contexto.font = 'bold 14px Arial';
        contexto.fillText(`${semana.registros.length} CHECK-IN${semana.registros.length === 1 ? '' : 'S'}`, x + 28, y + 315);
      } else {
        contexto.fillStyle = '#94a3b8';
        contexto.font = 'bold 30px Arial';
        contexto.fillText('Pausa na jornada', x + 28, y + 165);
        contexto.font = '17px Arial';
        contexto.fillText('Sem registros nesta semana.', x + 28, y + 198);
        contexto.fillStyle = '#e5eaf0';
        for (let barra = 0; barra < 5; barra++) {
          arredondar(x + 28 + barra * 72, y + 245, 54, 10, 5);
          contexto.fill();
        }
      }
    });

    const rodapeY = 350 + Math.ceil(totalSemanas / colunas) * (alturaCard + espaco) + 32;
    contexto.fillStyle = '#64748b';
    contexto.font = 'bold 15px Arial';
    contexto.textAlign = 'center';
    contexto.fillText(
      melhorSemana ? `MELHOR MOMENTO: SEMANA ${String(semanas.indexOf(melhorSemana) + 1).padStart(2, '0')}  •  ${textoVariacao(melhorSemana.variacao!)}` : 'CONSISTÊNCIA É TREINO  •  RESULTADO É HÁBITO',
      largura / 2,
      rodapeY
    );
    contexto.textAlign = 'left';
    const nomeArquivo = `${this.mes.getFullYear()}-${String(this.mes.getMonth() + 1).padStart(2, '0')}`;
    const link = document.createElement('a');
    link.download = `relatorio-peso-${nomeArquivo}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  public baixarImagemRelatorioCompleto(): void {
    const dados = [...this.pesos].sort((primeiro, segundo) => primeiro.dtPeso.localeCompare(segundo.dtPeso));

    if (!dados.length) {
      this.snack.exibirErro('Não há pesos registrados para gerar o relatório.');
      return;
    }

    const meses = Array.from(new Set(dados.map(peso => peso.dtPeso.substring(0, 7)))).map(chave => {
      const [ano, mes] = chave.split('-').map(Number);
      const dataMes = new Date(ano, mes - 1, 1);
      const registros = dados.filter(peso => peso.dtPeso.substring(0, 7) === chave);
      const primeiroDia = dataMes.getDay();
      const ultimoDia = new Date(ano, mes, 0).getDate();
      const totalSemanas = Math.ceil((primeiroDia + ultimoDia) / 7);
      const semanas = Array.from({ length: totalSemanas }, (_, indice) => {
        const inicio = Math.max(1, indice * 7 - primeiroDia + 1);
        const fim = Math.min(ultimoDia, (indice + 1) * 7 - primeiroDia);
        const itens = registros.filter(peso => {
          const dia = this.dataPeso(peso).getDate();
          return dia >= inicio && dia <= fim;
        });
        const ultimo = itens[itens.length - 1];
        const indiceUltimo = ultimo ? dados.findIndex(peso => peso.id === ultimo.id) : -1;
        const anterior = indiceUltimo > 0 ? dados[indiceUltimo - 1] : undefined;

        return { inicio, fim, itens, ultimo, variacao: ultimo && anterior ? ultimo.vlPeso - anterior.vlPeso : undefined };
      });

      return {
        dataMes,
        registros,
        semanas,
        variacao: registros[registros.length - 1].vlPeso - registros[0].vlPeso
      };
    });
    const largura = 1600;
    const margem = 70;
    const espaco = 26;
    const larguraCard = (largura - margem * 2 - espaco) / 2;
    const alturaCard = 420;
    const altura = 330 + Math.ceil(meses.length / 2) * (alturaCard + espaco) + 70;
    const canvas = document.createElement('canvas');
    const contexto = canvas.getContext('2d');

    if (!contexto) {
      return;
    }

    const arredondar = (x: number, y: number, larguraCaixa: number, alturaCaixa: number, raio: number): void => {
      contexto.beginPath();
      contexto.moveTo(x + raio, y);
      contexto.lineTo(x + larguraCaixa - raio, y);
      contexto.quadraticCurveTo(x + larguraCaixa, y, x + larguraCaixa, y + raio);
      contexto.lineTo(x + larguraCaixa, y + alturaCaixa - raio);
      contexto.quadraticCurveTo(x + larguraCaixa, y + alturaCaixa, x + larguraCaixa - raio, y + alturaCaixa);
      contexto.lineTo(x + raio, y + alturaCaixa);
      contexto.quadraticCurveTo(x, y + alturaCaixa, x, y + alturaCaixa - raio);
      contexto.lineTo(x, y + raio);
      contexto.quadraticCurveTo(x, y, x + raio, y);
      contexto.closePath();
    };
    const corVariacao = (valor: number): string => valor < 0 ? '#10b981' : valor > 0 ? '#f43f5e' : '#64748b';
    const textoVariacao = (valor: number): string => `${valor > 0 ? '+' : valor < 0 ? '-' : ''}${this.formatarPeso(Math.abs(valor))} kg`;
    const nomeMes = (data: Date): string => {
      const nome = data.toLocaleDateString('pt-BR', { month: 'long' });
      return nome[0].toUpperCase() + nome.slice(1);
    };
    const primeiro = dados[0];
    const ultimo = dados[dados.length - 1];
    const variacaoTotal = ultimo.vlPeso - primeiro.vlPeso;

    canvas.width = largura;
    canvas.height = altura;
    contexto.fillStyle = '#f4f7fb';
    contexto.fillRect(0, 0, largura, altura);
    const cabecalho = contexto.createLinearGradient(0, 0, largura, 270);
    cabecalho.addColorStop(0, '#071d35');
    cabecalho.addColorStop(.55, '#135a80');
    cabecalho.addColorStop(1, '#159a70');
    arredondar(margem, margem, largura - margem * 2, 210, 34);
    contexto.fillStyle = cabecalho;
    contexto.fill();
    contexto.save();
    contexto.globalAlpha = .13;
    contexto.strokeStyle = '#fff';
    contexto.lineWidth = 3;
    for (let indice = 0; indice < 7; indice++) {
      contexto.beginPath();
      contexto.arc(190 + indice * 92, 183, 132 + indice * 15, Math.PI * 1.08, Math.PI * 1.84);
      contexto.stroke();
    }
    contexto.restore();
    contexto.fillStyle = '#b5f5db';
    contexto.font = 'bold 18px Arial';
    contexto.fillText('JORNADA COMPLETA  •  PESO & PERFORMANCE', margem + 42, 124);
    contexto.fillStyle = '#fff';
    contexto.font = 'bold 46px Arial';
    contexto.fillText(`${nomeMes(meses[0].dataMes)} ${meses[0].dataMes.getFullYear()} — ${nomeMes(meses[meses.length - 1].dataMes)} ${meses[meses.length - 1].dataMes.getFullYear()}`, margem + 42, 181);
    contexto.fillStyle = '#d6f7e5';
    contexto.font = 'bold 16px Arial';
    contexto.fillText('UM MÊS DE CADA VEZ  •  CADA SEMANA CONTA', margem + 44, 220);

    const indicador = (x: number, rotulo: string, valor: string, cor: string): void => {
      arredondar(x, 105, 225, 112, 21);
      contexto.fillStyle = '#fff';
      contexto.globalAlpha = .14;
      contexto.fill();
      contexto.globalAlpha = 1;
      contexto.fillStyle = '#d6f7e5';
      contexto.font = 'bold 14px Arial';
      contexto.fillText(rotulo.toUpperCase(), x + 20, 139);
      contexto.fillStyle = '#fff';
      contexto.font = 'bold 28px Arial';
      contexto.fillText(valor, x + 20, 180);
      contexto.fillStyle = cor;
      contexto.beginPath();
      contexto.arc(x + 195, 129, 8, 0, Math.PI * 2);
      contexto.fill();
    };

    indicador(825, 'Último peso', `${this.formatarPeso(ultimo.vlPeso)} kg`, '#b5f5db');
    indicador(1065, 'Evolução total', textoVariacao(variacaoTotal), corVariacao(variacaoTotal));
    indicador(1305, 'Registros', String(dados.length).padStart(2, '0'), '#fde68a');

    meses.forEach((mes, indice) => {
      const coluna = indice % 2;
      const linha = Math.floor(indice / 2);
      const x = margem + coluna * (larguraCard + espaco);
      const y = 315 + linha * (alturaCard + espaco);
      const cor = corVariacao(mes.variacao);
      const ultimoMes = mes.registros[mes.registros.length - 1];

      contexto.save();
      contexto.shadowColor = '#cbd5e1';
      contexto.shadowBlur = 16;
      contexto.shadowOffsetY = 6;
      arredondar(x, y, larguraCard, alturaCard, 28);
      contexto.fillStyle = '#fff';
      contexto.fill();
      contexto.restore();
      const faixa = contexto.createLinearGradient(x, y, x + larguraCard, y + 106);
      faixa.addColorStop(0, `${cor}24`);
      faixa.addColorStop(1, '#ffffff');
      arredondar(x, y, larguraCard, 106, 28);
      contexto.fillStyle = faixa;
      contexto.fill();
      contexto.fillStyle = cor;
      arredondar(x, y + 25, 7, 54, 4);
      contexto.fill();
      contexto.fillStyle = '#172033';
      contexto.font = 'bold 30px Arial';
      contexto.fillText(`${nomeMes(mes.dataMes)} ${mes.dataMes.getFullYear()}`, x + 30, y + 44);
      contexto.fillStyle = '#64748b';
      contexto.font = 'bold 14px Arial';
      contexto.fillText(`${mes.registros.length} CHECK-IN${mes.registros.length === 1 ? '' : 'S'}  •  ÚLTIMO: ${this.formatarPeso(ultimoMes.vlPeso)} kg`, x + 30, y + 72);
      arredondar(x + larguraCard - 136, y + 27, 106, 34, 17);
      contexto.fillStyle = `${cor}1c`;
      contexto.fill();
      contexto.fillStyle = cor;
      contexto.font = 'bold 16px Arial';
      contexto.textAlign = 'center';
      contexto.fillText(textoVariacao(mes.variacao), x + larguraCard - 83, y + 50);
      contexto.textAlign = 'left';

      mes.semanas.forEach((semana, indiceSemana) => {
        const colunaSemana = indiceSemana % 3;
        const linhaSemana = Math.floor(indiceSemana / 3);
        const larguraSemana = (larguraCard - 72) / 3;
        const alturaSemana = 122;
        const xSemana = x + 24 + colunaSemana * (larguraSemana + 12);
        const ySemana = y + 126 + linhaSemana * (alturaSemana + 12);
        const corSemana = semana.variacao === undefined ? '#94a3b8' : corVariacao(semana.variacao);

        arredondar(xSemana, ySemana, larguraSemana, alturaSemana, 18);
        contexto.fillStyle = semana.ultimo ? '#f8fafc' : '#f1f5f9';
        contexto.fill();
        contexto.fillStyle = corSemana;
        arredondar(xSemana, ySemana + 16, 4, alturaSemana - 32, 2);
        contexto.fill();
        contexto.font = 'bold 13px Arial';
        contexto.fillStyle = '#64748b';
        contexto.fillText(`SEM ${String(indiceSemana + 1).padStart(2, '0')}  •  ${String(semana.inicio).padStart(2, '0')}—${String(semana.fim).padStart(2, '0')}`, xSemana + 16, ySemana + 28);
        if (semana.ultimo) {
          contexto.fillStyle = '#172033';
          contexto.font = 'bold 24px Arial';
          contexto.fillText(`${this.formatarPeso(semana.ultimo.vlPeso)} kg`, xSemana + 16, ySemana + 62);
          contexto.fillStyle = corSemana;
          contexto.font = 'bold 16px Arial';
          contexto.fillText(semana.variacao === undefined ? '—' : textoVariacao(semana.variacao), xSemana + 16, ySemana + 91);
          contexto.fillStyle = '#94a3b8';
          contexto.font = 'bold 12px Arial';
          contexto.fillText(`${semana.itens.length} CHECK-IN${semana.itens.length === 1 ? '' : 'S'}`, xSemana + 16, ySemana + 109);
        } else {
          contexto.fillStyle = '#94a3b8';
          contexto.font = 'bold 15px Arial';
          contexto.fillText('Sem registro', xSemana + 16, ySemana + 66);
        }
      });
    });

    const rodapeY = 315 + Math.ceil(meses.length / 2) * (alturaCard + espaco) + 20;
    contexto.fillStyle = '#64748b';
    contexto.font = 'bold 15px Arial';
    contexto.textAlign = 'center';
    contexto.fillText('EVOLUÇÃO NÃO É PRESSA  •  É CONSISTÊNCIA', largura / 2, rodapeY);
    contexto.textAlign = 'left';
    const link = document.createElement('a');
    link.download = 'relatorio-completo-peso.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  public get tituloMes(): string {
    const titulo = this.mes.toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric'
    });

    return titulo[0].toUpperCase() + titulo.slice(1);
  }

  public get dias(): Date[] {
    const inicio = new Date(
      this.mes.getFullYear(),
      this.mes.getMonth(),
      1 - this.mes.getDay()
    );

    return Array.from(
      { length: 42 },
      (_, indice) => new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + indice)
    );
  }

  public mudarMes(valor: number): void {
    this.mes = new Date(this.mes.getFullYear(), this.mes.getMonth() + valor, 1);
    this.cancelar();
  }

  public pesoDoDia(dia: Date): BeControlePeso | undefined {
    return this.pesos.find(peso => this.mesmoDia(this.dataPeso(peso), dia));
  }

  public iniciarNovo(dia: Date): void {
    if (this.dataFutura(dia)) {
      return;
    }

    const existente = this.pesoDoDia(dia);

    if (existente) {
      this.editar(existente, new MouseEvent('click'));
      return;
    }

    this.editando = {
      id: null,
      dtPeso: this.chave(dia),
      dtPesoData: new Date(dia),
      vlPeso: 0
    };
    this.chaveDiaEdicao = this.chave(dia);
  }

  public editar(peso: BeControlePeso, evento: MouseEvent): void {
    evento.stopPropagation();
    this.editando = { ...peso, dtPesoData: this.dataPeso(peso) };
    this.chaveDiaEdicao = this.chave(this.dataPeso(peso));
  }

  public cancelar(): void {
    this.editando = undefined;
    this.chaveDiaEdicao = undefined;
  }

  public salvar(): void {
    if (!this.editando?.dtPesoData || this.editando.vlPeso < 0 || this.editando.vlPeso > 300) {
      return;
    }

    if (this.dataFutura(this.editando.dtPesoData)) {
      this.snack.exibirErro('Não é possível registrar peso em uma data futura.');
      return;
    }

    const payload = {
      ...this.editando,
      dtPeso: this.chave(this.editando.dtPesoData)
    };
    const requisicao = payload.id
      ? this.http.put<BeControlePeso>('/api/ControlePesos', payload)
      : this.http.post<BeControlePeso>('/api/ControlePesos', payload);

    requisicao.subscribe({
      next: peso => {
        const item = this.preparar(peso);
        const indice = this.pesos.findIndex(atual => atual.id === item.id);

        if (indice >= 0) {
          this.pesos[indice] = item;
        } else {
          this.pesos.push(item);
        }

        this.atualizarGrafico();
        this.cancelar();
        this.snack.exibirSucesso('Peso salvo!');
      },
      error: erro => this.snack.exibirErro(
        erro?.error?.message ?? erro?.error ?? 'Não foi possível salvar o peso.'
      )
    });
  }

  public excluir(peso?: BeControlePeso): void {
    const alvo = peso ?? this.editando;

    if (!alvo?.id) {
      return;
    }

    const data: DialogData = {
      title: 'Alerta',
      message: 'Deseja excluir esse peso?',
      buttonText: 'Não',
      secondButtonText: 'Sim'
    };

    this.dialog.open(DialogComponent, { data }).afterClosed().subscribe(confirmado => {
      if (!confirmado) {
        return;
      }

      this.http.delete(`/api/ControlePesos/${alvo.id}`).subscribe({
        next: () => {
          this.pesos = this.pesos.filter(item => item.id !== alvo.id);
          this.atualizarGrafico();
          this.cancelar();
          this.snack.exibirSucesso('Peso excluído!');
        },
        error: () => this.snack.exibirErro('Não foi possível excluir o peso.')
      });
    });
  }

  public iniciarLinha(peso: BeControlePeso): void {
    if (!peso.id) {
      peso.dtPesoData = new Date();
      peso.vlPeso = 0;
    }
  }

  public salvarLinha(peso: BeControlePeso): void {
    this.editando = peso;
    this.salvar();
  }

  public readonly rastrearDia = (_: number, dia: Date): string => this.chave(dia);

  public abrirBalaoParaCima(dia: Date): boolean {
    return this.dias.findIndex(item => this.chave(item) === this.chave(dia)) >= 21;
  }

  public chave(data: Date): string {
    return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
  }

  private carregar(): void {
    this.http.get<BeControlePeso[]>('/api/ControlePesos').subscribe({
      next: resposta => {
        this.pesos = resposta.map(peso => this.preparar(peso));
        this.atualizarGrafico();
      },
      error: () => this.snack.exibirErro('Não foi possível carregar os pesos.')
    });
  }

  private carregarAltura(): void {
    this.http.get<{ altura?: number }>('/api/ControlePesos/Altura').subscribe({
      next: resposta => this.altura = resposta.altura
    });
  }

  private preparar(peso: BeControlePeso): BeControlePeso {
    return { ...peso, dtPesoData: this.dataPeso(peso) };
  }

  private dataPeso(peso: BeControlePeso): Date {
    return new Date(`${peso.dtPeso.split('T')[0]}T00:00:00`);
  }

  private mesmoDia(dataA: Date, dataB: Date): boolean {
    return this.chave(dataA) === this.chave(dataB);
  }

  private dataFutura(data: Date): boolean {
    return this.chave(data) > this.chave(this.hoje);
  }

  private obterInformacaoImc(peso: number): { valor: number; estado: string; pesoMinimo: number; pesoMaximo: number; cor: string } | undefined {
    if (!this.altura) {
      return undefined;
    }

    const valor = peso / (this.altura * this.altura);
    const estado = valor < 18.5 ? 'Baixo peso' : valor < 25 ? 'Normal' : valor < 30 ? 'Sobrepeso' : valor < 35 ? 'Obesidade I' : valor < 40 ? 'Obesidade II' : 'Obesidade III';
    const cor = valor < 18.5 ? '#f59e0b' : valor < 25 ? '#10b981' : '#f43f5e';

    return { valor, estado, pesoMinimo: 18.5 * this.altura * this.altura, pesoMaximo: 24.9 * this.altura * this.altura, cor };
  }

  private formatarPeso(valor: number): string {
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  private formatarData(data: string): string {
    const [, mes, dia] = data.split('T')[0].split('-');

    return `${dia}/${mes}`;
  }

  private atualizarGrafico(): void {
    const valores = this.pesos.map(peso => peso.vlPeso);
    const minimo = valores.length ? Math.max(0, Math.floor(Math.min(...valores) - 5)) : 0;
    const maximo = valores.length ? Math.ceil(Math.max(...valores) + 5) : 5;

    this.chartYaxis = {
      min: minimo,
      max: maximo,
      title: { text: 'kg' }
    };
    this.chartSeries = [{
      name: 'Peso (kg)',
      data: [...this.pesos]
        .sort((primeiro, segundo) => primeiro.dtPeso.localeCompare(segundo.dtPeso))
        .map(peso => ({
          x: this.dataPeso(peso).getTime(),
          y: peso.vlPeso
        }))
    }];
  }
}
